import {
  formatLocaleCurrency,
  FormatLocaleCurrencyOptions,
} from "../../format-locale-currency/format-locale-currency";

export interface MoneyData {
  /**
   * The integer value of the amount
   */
  integer: number;

  /**
   * The currency code
   */
  currency: string;

  /**
   * The formatted value of the amount
   */
  value: string;
}

interface MoneyOperations {
  /**
   * Returns the formatted currency value with 2 decimal places
   * Includes currency symbol and formatted numeric value
   */
  currencyValue: string;

  /**
   * Returns the raw unformatted numeric value
   * This is the original amount passed to the Money instance
   */
  rawValue: number;

  /**
   * Indicates whether the money value is zero
   * @returns true if the amount equals zero, false otherwise
   */
  isZero: boolean;

  /**
   * Indicates whether the money value is positive
   * @returns true if the amount is greater than zero, false otherwise
   */
  isPositive: boolean;

  /**
   * Adds the specified amount to the current money value
   * @param amount - The amount to add (can be a number or another Money instance)
   * @returns A new Money instance with the sum
   * @example
   * const money = new Money(100);
   * const result = money.add(50); // Money instance with value 150
   * const result2 = money.add(new Money(25)); // Money instance with value 125
   */
  add(amount: number | Money): Money;

  /**
   * Subtracts the specified amount from the current money value
   * @param amount - The amount to subtract (can be a number or another Money instance)
   * @returns A new Money instance with the difference
   * @example
   * const money = new Money(100);
   * const result = money.subtract(30); // Money instance with value 70
   * const result2 = money.subtract(new Money(25)); // Money instance with value 75
   */
  subtract(amount: number | Money): Money;

  /**
   * Multiplies the current money value by the specified count
   * @param count - The multiplier
   * @returns A new Money instance with the product
   * @example
   * const money = new Money(50);
   * const result = money.times(3); // Money instance with value 150
   */
  times(count: number): Money;

  /**
   * Divides the current money value by the specified count
   * @param count - The divisor
   * @returns A new Money instance with the quotient (0 if count is 0)
   * @example
   * const money = new Money(100);
   * const result = money.div(4); // Money instance with value 25
   * const zero = money.div(0); // Money instance with value 0
   */
  divideBy(count: number): Money;

  /**
   * Calculates a percentage of the current money value
   * @param amount - The percentage to calculate (e.g., 10 for 10%)
   * @returns A new Money instance representing the percentage amount
   * @example
   * const money = new Money(200);
   * const result = money.percentage(15); // Money instance with value 30 (15% of 200)
   */
  percentage(amount: number): Money;

  /**
   * Formats the money value as a string with currency symbol
   * @param precision - Optional number of decimal places to display
   * @returns Formatted string with currency symbol and value
   * @example
   * const money = new Money(1234.56, { currency: 'USD' });
   * money.format(2); // "$1,234.56"
   */
  format(precision?: number): string;

  /**
   * Converts the money value to a float with specified decimal precision
   * Uses Intl.NumberFormat for accurate rounding and formatting
   * @param decimals - Number of decimal places (defaults to 5 for API requests)
   * @returns The numeric value rounded to the specified decimal places
   * @example
   * const money = new Money(1000);
   * money.toFloat(); // 1000 (internally formatted as 1000.00000)
   * const money2 = new Money(1000.123);
   * money2.toFloat(); // 1000.123 (internally formatted as 1000.12300)
   * const money3 = new Money(1000.123456);
   * money3.toFloat(); // 1000.12346 (rounded to 5 decimals)
   */
  toFloat(decimals?: number): number;

  /**
   * Converts the money value to a display string with currency symbol
   * Uses the format method with precision based on the enableHighPrecision flag
   * @param enableHighPrecision - When true, shows 2 decimal places; when false, shows 0 decimals (defaults to true)
   * @returns Formatted string suitable for display (e.g., "Rp1,000.00" or "Rp1,000")
   * @example
   * const money = new Money(1000.50, { currency: 'IDR' });
   * money.toDisplay(); // "Rp1,000.50" (with precision)
   * money.toDisplay(false); // "Rp1,000" (without precision)
   */
  toDisplay(enableHighPrecision?: boolean): string;

  /**
   * Returns a new Money instance with a rounded value
   * @param roundingFn - Optional custom rounding function (defaults to Math.round)
   * @returns A new Money instance with the rounded amount
   * @example
   * const money = new Money(123.67);
   * const rounded = money.rounded(); // Money instance with value 124
   * const floored = money.rounded(Math.floor); // Money instance with value 123
   */
  rounded(roundingFn?: (amount: number) => number): Money;
}

export class Money implements MoneyData, MoneyOperations {
  data: MoneyData;

  constructor(
    private amount: number = 0,
    private options: FormatLocaleCurrencyOptions = {}
  ) {
    this.data = this.formatMoney();
  }

  get integer() {
    return this.data.integer;
  }

  get currency() {
    return this.data.currency;
  }

  get value() {
    return this.data.value;
  }

  /**
   * @deprecated Use `toDisplay()` instead for consistent formatting with options
   */
  get currencyValue(): string {
    return this.currency + this.value;
  }

  get rawValue(): number {
    return this.amount;
  }

  get isZero() {
    return this.amount === 0;
  }

  get isPositive() {
    return this.amount > 0;
  }

  add(amount: number | Money) {
    let total: number;

    if (typeof amount === "number") {
      total = this.amount + amount;
    } else {
      total = this.amount + amount.amount;
    }

    return new Money(total, this.options);
  }

  subtract(amount: number | Money) {
    let total: number;

    if (typeof amount === "number") {
      total = this.amount - amount;
    } else {
      total = this.amount - amount.amount;
    }

    return new Money(total, this.options);
  }

  times(count: number) {
    const total = this.amount * count;

    return new Money(total, this.options);
  }

  divideBy(count: number) {
    if (count === 0) return new Money(0, this.options);
    return new Money(this.amount / count, this.options);
  }

  percentage(amount: number) {
    const total = this.amount * (amount / 100);

    return new Money(total, this.options);
  }

  format(precision?: number): string {
    const formatted = formatLocaleCurrency(this.amount, {
      ...this.options,
      ...(precision !== undefined && { precision }),
    });

    const valueMatch = removeZerotrailingZeros(
      formatted.match(/[\d,.]+/g)?.join("") || ""
    );
    const currencyMatch = formatted.match(/[^\d.,\s]+/)?.join("") || "";

    return currencyMatch + valueMatch;
  }

  toFloat(decimals = 5): number {
    const formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: false,
    });
    return parseFloat(formatter.format(this.amount));
  }

  toDisplay(enableHighPrecision = true): string {
    return this.format(enableHighPrecision ? 2 : 0);
  }

  rounded(roundingFn: (amount: number) => number = Math.round): Money {
    return new Money(roundingFn(this.amount), this.options);
  }

  /**
   * Formats the money value into a MoneyData object
   * @param enableHighPrecision - When true, uses 2 decimal places; when false, uses 0 decimals
   * @returns MoneyData object containing integer value, currency symbol, and formatted value
   * @private
   */
  private formatMoney(enableHighPrecision = false): MoneyData {
    let precision = enableHighPrecision ? 2 : 0; // Default precision for display

    if (this.options.precision !== undefined) {
      precision = this.options.precision;
    } else if (this.options.rounded) {
      precision = 0;
    }

    const formatted = formatLocaleCurrency(this.amount, {
      locale: this.options.locale,
      currency: this.options.currency,
      precision,
    });

    const valueMatch = removeZerotrailingZeros(
      formatted.match(/[\d,.]+/g)?.join("") || ""
    );
    const currencyMatch = formatted.match(/[^\d.,\s]+/)?.join("") || "";

    return {
      integer: Math.round(this.amount),
      currency: currencyMatch,
      value: valueMatch,
    };
  }
}

export default Money;

const removeZerotrailingZeros = (value: string): string => {
  let result = value.replace(/,(\d*?[1-9])0+$/, ",$1");
  result = result.replace(/,0+$/, "");
  return result;
};
